import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../context/AuthContext';
import PageHeader   from '../../../components/common/PageHeader';
import AlertToast   from '../../../components/common/AlertToast';
import LoadingSpinner from '../../../components/common/LoadingSpinner';

// ── Helpers ───────────────────────────────────────────────────────────────────
const STATUS_MAP = {
  proposed:    { bg: 'warning',   label: 'Proposed'    },
  approved:    { bg: 'success',   label: 'Approved'    },
  rejected:    { bg: 'danger',    label: 'Rejected'     },
  cancelled:   { bg: 'secondary', label: 'Cancelled'   },
  auto_rejected: { bg: 'warning', label: 'Not Approved Leave' },
};

const LEAVE_TYPES = [
  { value: 'casual',        label: 'Casual Leave'      },
  { value: 'sick',          label: 'Sick Leave'        },
  { value: 'earned',        label: 'Earned Leave'      },
  { value: 'unpaid',        label: 'Unpaid Leave'      },
  { value: 'work_from_home',label: 'Work From Home'    },
  { value: 'permission',    label: 'Permission'        },
  { value: 'maternity',     label: 'Maternity Leave'   },
  { value: 'paternity',     label: 'Paternity Leave'   },
];

const DURATION_OPTS = [
  { value: 'full_day', label: 'Full Day' },
  { value: 'morning_half_day', label: 'Morning Half Day' },
  { value: 'afternoon_half_day', label: 'Afternoon Half Day' },
  { value: 'hours',    label: 'Hours'    },
];

const fmt = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const fmtTime = (d) => d ? new Date(d).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '';

const fmtDateTime = (d) => d ? `${new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} at ${fmtTime(d)}` : '—';

const isWithin2Hours = (appliedAt) => {
  if (!appliedAt) return false;
  const twoHoursAgo = Date.now() - (2 * 60 * 60 * 1000);
  return new Date(appliedAt).getTime() > twoHoursAgo;
};

const getDurationLabel = (duration) => {
  const opt = DURATION_OPTS.find(d => d.value === duration);
  return opt ? opt.label : duration;
};

const StatusBadge = ({ status }) => {
  const s = STATUS_MAP[status] || { bg: 'secondary', label: status };
  return <span className={`badge bg-${s.bg}`}>{s.label}</span>;
};

const LeaveTypeBadge = ({ type }) => {
  const label = LEAVE_TYPES.find(l => l.value === type)?.label || type;
  return <span className="badge bg-info text-dark">{label}</span>;
};

// ── AuditLog panel ────────────────────────────────────────────────────────────
function AuditLog({ log = [] }) {
  if (!log.length) return <p className="text-muted small mb-0">No audit trail.</p>;
  return (
    <ul className="list-unstyled mb-0">
      {log.map((entry, i) => (
        <li key={i} className="d-flex gap-2 align-items-start mb-1" style={{ fontSize: 12 }}>
          <span className="badge bg-light text-dark text-capitalize" style={{ minWidth: 70 }}>{entry.action}</span>
          <span className="text-muted">
            {entry.performedBy?.firstName
              ? `${entry.performedBy.firstName} ${entry.performedBy.lastName}`
              : 'System'}
            {' — '}
            {fmt(entry.performedAt)}
            {entry.note && <> · <em>{entry.note}</em></>}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
export default function LeavePage() {
  const { user, isAdmin, role } = useAuth();
  const isManager = isAdmin || ['manager', 'team_lead'].includes(role?.key);

  // ── State ──────────────────────────────────────────────────────────────────
  const [tab, setTab]           = useState(0);
  const [leaves, setLeaves]     = useState([]);
  const [allLeaves, setAllLeaves] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [form, setForm]         = useState({
    leaveType: 'casual', leaveDuration: 'full_day',
    startDate: '', endDate: '', reason: '',
    permissionHours: 0, isEmergency: false, emergencyNote: '',
    proofFile: null,
  });
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [reviewDlg, setReviewDlg] = useState(null); // { leave, action: 'approve'|'reject' }
  const [reviewNote, setReviewNote] = useState('');
  const [reviewSaving, setReviewSaving] = useState(false);
  const [filterStatus, setFilterStatus]     = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [staff, setStaff]       = useState([]);
  const [alert, setAlert]       = useState({ show: false, msg: '', type: 'success' });
  const [expandedId, setExpandedId] = useState(null);
  const [editLeave, setEditLeave] = useState(null); // leave being edited
  const [adminEditMode, setAdminEditMode] = useState(false);
  const modalRef = useRef(null);

  const showAlert = (msg, type = 'success') => setAlert({ show: true, msg, type });
  const f         = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // ── Load own leaves ────────────────────────────────────────────────────────
  const loadLeaves = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/hrm/leave');
      setLeaves(r.data?.records || r.data?.data || []);
    } catch { setLeaves([]); }
    setLoading(false);
  }, []);

  // ── Load all leaves (admin/manager) ───────────────────────────────────────
  const loadAllLeaves = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus)   params.status     = filterStatus;
      if (filterEmployee) params.employeeId = filterEmployee;
      const r = await api.get('/hrm/leave', { params });
      setAllLeaves(r.data?.records || r.data?.data || []);
    } catch { setAllLeaves([]); }
    setLoading(false);
  }, [filterStatus, filterEmployee]);

  // ── Load staff list (for admin employee filter - exclude students and parents) ───────────────────────────
  const loadStaff = useCallback(async () => {
    if (!isManager) return;
    try {
      const r = await api.get('/users', { params: { limit: 500, isActive: true } });
      const users = r.data?.records || r.data?.data || [];
      // Filter out students and parents - only show employees (non-students/parents)
      const excludedRoles = ['student', 'parent'];
      const employees = users.filter(u =>
        !excludedRoles.includes(u.userType) && !excludedRoles.includes(u.roleKey)
      );
      setStaff(employees);
    } catch { /* ignore */ }
  }, [isManager]);

  useEffect(() => { loadLeaves(); loadStaff(); }, [loadLeaves, loadStaff]);
  useEffect(() => { if (tab === 1 && isManager) loadAllLeaves(); }, [tab, isManager, loadAllLeaves]);

  // ── Submit leave ───────────────────────────────────────────────────────────
  const submitLeave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };

      // Set halfDaySession for half day leaves
      if (payload.leaveDuration === 'morning_half_day') {
        payload.halfDaySession = 'morning';
        payload.totalDays = 0.5;
        payload.endDate = payload.startDate;
      } else if (payload.leaveDuration === 'afternoon_half_day') {
        payload.halfDaySession = 'afternoon';
        payload.totalDays = 0.5;
        payload.endDate = payload.startDate;
      } else if (payload.leaveDuration === 'hours' && payload.startDate && !payload.endDate) {
        payload.endDate = payload.startDate;
      }

      // Use FormData for multipart submission (file upload support)
      const formData = new FormData();
      Object.keys(payload).forEach(key => {
        if (payload[key] !== null && payload[key] !== undefined) {
          formData.append(key, payload[key]);
        }
      });

      // Check if proof file is selected
      const proofInput = document.getElementById('proofFile');
      if (proofInput && proofInput.files && proofInput.files[0]) {
        formData.append('proof', proofInput.files[0]);
      }

      // Don't set Content-Type manually - axios will set it with correct boundary for FormData
      await api.post('/hrm/leave', formData);
      showAlert('Leave application submitted successfully!');
      setShowForm(false);
      setForm({ leaveType: 'casual', leaveDuration: 'full_day', startDate: '', endDate: '', reason: '', permissionHours: 0, isEmergency: false, emergencyNote: '', proofFile: null });
      if (proofInput) proofInput.value = '';
      loadLeaves();
    } catch (err) {
      showAlert(err.response?.data?.message || 'Failed to submit leave', 'danger');
    }
    setSaving(false);
  };

  // ── Cancel own leave ───────────────────────────────────────────────────────
  const cancelLeave = async (leave, { adminMode = false } = {}) => {
    if (adminMode) {
      const note = window.prompt('Reason for cancellation (optional):', '');
      if (note === null) return;
      try {
        await api.patch(`/hrm/leave/${leave._id}/cancel`, note.trim() ? { note } : {});
        showAlert('Leave cancelled');
        loadAllLeaves();
        loadLeaves();
      } catch (err) {
        showAlert(err.response?.data?.message || 'Failed to cancel', 'danger');
      }
      return;
    }

    if (!isWithin2Hours(leave.appliedAt)) {
      showAlert('Cannot cancel: Leave can only be cancelled within 2 hours of application', 'danger');
      return;
    }
    if (!window.confirm('Cancel this leave application?')) return;
    try {
      await api.patch(`/hrm/leave/${leave._id}/cancel`);
      showAlert('Leave cancelled');
      loadLeaves();
      if (tab === 1) loadAllLeaves();
    } catch (err) {
      showAlert(err.response?.data?.message || 'Failed to cancel', 'danger');
    }
  };

  // ── Edit own leave ────────────────────────────────────────────────────────
  const openEditLeave = (leave, { adminMode = false } = {}) => {
    if (!adminMode && !isWithin2Hours(leave.appliedAt)) {
      showAlert('Cannot edit: Leave can only be edited within 2 hours of application', 'danger');
      return;
    }
    setAdminEditMode(adminMode);
    setEditLeave(leave);
    setForm({
      leaveType: leave.leaveType || 'casual',
      leaveDuration: leave.leaveDuration || 'full_day',
      startDate: leave.startDate ? new Date(leave.startDate).toISOString().split('T')[0] : '',
      endDate: leave.endDate ? new Date(leave.endDate).toISOString().split('T')[0] : '',
      reason: leave.reason || '',
      permissionHours: leave.permissionHours || 0,
      isEmergency: leave.isEmergency || false,
      emergencyNote: leave.emergencyNote || '',
      proofFile: null,
    });
    setShowForm(true);
  };

  const updateLeave = async (e) => {
    e.preventDefault();
    if (!editLeave) return;
    setSaving(true);
    try {
      const payload = { ...form };

      // Set halfDaySession for half day leaves
      if (payload.leaveDuration === 'morning_half_day') {
        payload.halfDaySession = 'morning';
        payload.totalDays = 0.5;
        payload.endDate = payload.startDate;
      } else if (payload.leaveDuration === 'afternoon_half_day') {
        payload.halfDaySession = 'afternoon';
        payload.totalDays = 0.5;
        payload.endDate = payload.startDate;
      } else if (payload.leaveDuration === 'hours' && payload.startDate && !payload.endDate) {
        payload.endDate = payload.startDate;
      }

      const formData = new FormData();
      Object.keys(payload).forEach(key => {
        if (payload[key] !== null && payload[key] !== undefined) {
          formData.append(key, payload[key]);
        }
      });

      const proofInput = document.getElementById('proofFile');
      if (proofInput && proofInput.files && proofInput.files[0]) {
        formData.append('proof', proofInput.files[0]);
      }

      await api.patch(`/hrm/leave/${editLeave._id}`, formData);
      showAlert('Leave updated successfully!');
      setShowForm(false);
      setEditLeave(null);
      setAdminEditMode(false);
      setForm({ leaveType: 'casual', leaveDuration: 'full_day', startDate: '', endDate: '', reason: '', permissionHours: 0, isEmergency: false, emergencyNote: '', proofFile: null });
      if (proofInput) proofInput.value = '';
      loadLeaves();
      if (tab === 1) loadAllLeaves();
    } catch (err) {
      showAlert(err.response?.data?.message || 'Failed to update leave', 'danger');
    }
    setSaving(false);
  };

  // ── Approve / Reject ───────────────────────────────────────────────────────
  const openReviewDlg = (leave, action) => {
    setReviewDlg({ leave, action });
    setReviewNote('');
  };

  const submitReview = async () => {
    if (!reviewDlg) return;
    setReviewSaving(true);
    const { leave, action } = reviewDlg;
    try {
      await api.patch(`/hrm/leave/${leave._id}/${action}`, { note: reviewNote });
      showAlert(`Leave ${action}d successfully`);
      setReviewDlg(null);
      loadAllLeaves();
      if (tab === 0) loadLeaves();
    } catch (err) {
      showAlert(err.response?.data?.message || `Failed to ${action} leave`, 'danger');
    }
    setReviewSaving(false);
  };

  // ── Render helpers ─────────────────────────────────────────────────────────
  const renderMyLeaves = () => {
    if (loading) return <LoadingSpinner />;
    if (!leaves.length) return (
      <div className="text-center py-5 text-muted">
        <i className="bi bi-calendar-x fs-2 d-block mb-2"></i>
        No leave applications yet
      </div>
    );

    return leaves.map(leave => {
      const applicantName = leave.employeeName || [leave.employee?.firstName, leave.employee?.lastName].filter(Boolean).join(' ') || user?.firstName;
      const isAutoRejected = leave.status === 'rejected' && leave.rejectionReason && leave.rejectionReason.includes('Applied less than 24 hours');
      const displayStatus = isAutoRejected ? 'auto_rejected' : leave.status;
      const hasProof = leave.proofUrl || leave.proofFilename;
      const canModify = leave.status === 'proposed' && isWithin2Hours(leave.appliedAt);
      const hoursLeft = leave.appliedAt ? Math.max(0, Math.floor((2 * 60 * 60 * 1000 - (Date.now() - new Date(leave.appliedAt).getTime())) / (60 * 60 * 1000))) : 0;
      const minutesLeft = leave.appliedAt ? Math.max(0, Math.floor((2 * 60 * 60 * 1000 - (Date.now() - new Date(leave.appliedAt).getTime())) / (60 * 1000)) % 60) : 0;

      return (
      <div key={leave._id} className={`card mb-3 border-0 shadow-sm ${isAutoRejected ? 'border-left border-4 border-warning' : ''}`}>
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <LeaveTypeBadge type={leave.leaveType} />
              <StatusBadge status={displayStatus} />
              {isAutoRejected && <span className="badge bg-warning text-dark">Admin review required</span>}
              {hasProof && <span className="badge bg-info text-dark"><i className="bi bi-paperclip me-1"></i>Proof</span>}
              {leave.status === 'proposed' && (
                <span className="badge bg-light text-dark">
                  {canModify ? `${hoursLeft}h ${minutesLeft}m left to edit` : 'Editing expired'}
                </span>
              )}
            </div>
            <div className="d-flex gap-2">
              {leave.status === 'proposed' && (
                <>
                  {canModify && (
                    <button className="btn btn-outline-primary btn-sm" onClick={() => openEditLeave(leave)}>
                      <i className="bi bi-pencil me-1"></i>Edit
                    </button>
                  )}
                  <button className="btn btn-outline-secondary btn-sm" onClick={() => cancelLeave(leave)} disabled={!canModify}>
                    <i className="bi bi-x-circle me-1"></i>Cancel
                  </button>
                </>
              )}
              <button className="btn btn-outline-info btn-sm"
                onClick={() => setExpandedId(expandedId === leave._id ? null : leave._id)}>
                <i className={`bi bi-chevron-${expandedId === leave._id ? 'up' : 'down'}`}></i>
              </button>
            </div>
          </div>

          {applicantName && (
            <div className="mt-2 d-flex align-items-center gap-1" style={{ fontSize: 13 }}>
              <i className="bi bi-person-fill text-muted" style={{ fontSize: 12 }}></i>
              <span className="fw-medium">{applicantName}</span>
            </div>
          )}

          <div className="row g-2 mt-2">
            <div className="col-sm-3">
              <small className="text-muted d-block">Leave Date</small>
              <strong style={{ fontSize: 13 }}>{fmt(leave.startDate)}</strong>
            </div>
            <div className="col-sm-2">
              <small className="text-muted d-block">Days</small>
              <strong style={{ fontSize: 13 }}>{leave.totalDays ? `${leave.totalDays} day(s)` : `${leave.permissionHours} hrs`}</strong>
            </div>
            <div className="col-sm-3">
              <small className="text-muted d-block">Duration</small>
              <strong style={{ fontSize: 13 }}>{getDurationLabel(leave.leaveDuration)}</strong>
            </div>
            <div className="col-sm-3">
              <small className="text-muted d-block">Type</small>
              <strong style={{ fontSize: 13 }}>{LEAVE_TYPES.find(l => l.value === leave.leaveType)?.label || leave.leaveType}</strong>
            </div>
            <div className="col-sm-4">
              <small className="text-muted d-block">Applied On</small>
              <strong style={{ fontSize: 13 }}>{fmtDateTime(leave.appliedAt)}</strong>
            </div>
          </div>

          <p className="mb-1 mt-2" style={{ fontSize: 13 }}>
            <span className="text-muted">Reason: </span>{leave.reason}
          </p>

          {leave.status === 'approved' && leave.approvedBy && (
            <p className="mb-0 text-success" style={{ fontSize: 12 }}>
              <i className="bi bi-check-circle me-1"></i>
              Approved by {leave.approvedBy.firstName} {leave.approvedBy.lastName} on {fmt(leave.approvedAt)}
            </p>
          )}
          {leave.status === 'rejected' && leave.rejectionReason && (
            <p className={`mb-0 ${isAutoRejected ? 'text-warning' : 'text-danger'}`} style={{ fontSize: 12 }}>
              <i className={`bi ${isAutoRejected ? 'bi-exclamation-circle' : 'bi-x-circle'} me-1`}></i>
              {isAutoRejected ? 'Not approved: ' : 'Rejected: '}{leave.rejectionReason}
            </p>
          )}

          {expandedId === leave._id && (
            <div className="mt-3 pt-3 border-top">
              <p className="text-muted mb-1" style={{ fontSize: 12, fontWeight: 600 }}>AUDIT LOG</p>
              <AuditLog log={leave.auditLog || []} />
            </div>
          )}
        </div>
      </div>
      );
    });
  };

  const renderAllLeaves = () => {
    if (loading) return <LoadingSpinner />;
    if (!allLeaves.length) return (
      <div className="text-center py-5 text-muted">
        <i className="bi bi-calendar-x fs-2 d-block mb-2"></i>
        No leave records found
      </div>
    );

    return (
      <div className="table-responsive">
        <table className="table table-hover align-middle" style={{ fontSize: 13 }}>
          <thead className="table-light">
            <tr>
              <th>Employee</th>
              <th>Leave Type</th>
              <th>Leave Date</th>
              <th>Days</th>
              <th>Status</th>
              <th>Applied On</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {allLeaves.map(leave => {
              const empName = leave.employeeName || leave.employee?.email || '—';
              const isAutoRejected = leave.status === 'rejected' && leave.rejectionReason && leave.rejectionReason.includes('Applied less than 24 hours');
              const displayStatus = isAutoRejected ? 'auto_rejected' : leave.status;
              const hasProof = leave.proofUrl || leave.proofFilename;
              return (
              <React.Fragment key={leave._id}>
                <tr className={isAutoRejected ? 'table-warning' : ''} style={{ cursor: 'pointer' }}
                    onClick={() => setExpandedId(expandedId === leave._id ? null : leave._id)}>
                  <td>
                    <div className="fw-medium">{empName}</div>
                    <small className="text-muted text-capitalize">
                      {leave.employee?.roleKey || leave.employee?.email || ''}
                    </small>
                    {hasProof && <span className="badge bg-info text-dark ms-1"><i className="bi bi-paperclip"></i></span>}
                  </td>
                  <td><LeaveTypeBadge type={leave.leaveType} /></td>
                  <td>{fmt(leave.startDate)}</td>
                  <td>{leave.totalDays || leave.permissionHours + ' hrs'}</td>
                  <td>
                    <StatusBadge status={displayStatus} />
                  </td>
                  <td>{fmtDateTime(leave.appliedAt || leave.createdAt)}</td>
                  <td>
                    <div className="d-flex gap-1 flex-wrap">
                      {['proposed', 'approved'].includes(leave.status) && (
                        <>
                          <button className="btn btn-outline-primary btn-sm"
                            title="Edit"
                            onClick={(e) => { e.stopPropagation(); openEditLeave(leave, { adminMode: true }); }}>
                            <i className="bi bi-pencil"></i>
                          </button>
                          <button className="btn btn-outline-danger btn-sm"
                            title="Cancel"
                            onClick={(e) => { e.stopPropagation(); cancelLeave(leave, { adminMode: true }); }}>
                            <i className="bi bi-x-circle"></i>
                          </button>
                        </>
                      )}
                      {(leave.status === 'proposed' || isAutoRejected) && (
                        <>
                          <button className="btn btn-success btn-sm"
                            title="Approve"
                            onClick={(e) => { e.stopPropagation(); openReviewDlg(leave, 'approve'); }}>
                            <i className="bi bi-check-lg"></i>
                          </button>
                          {leave.status === 'proposed' && (
                            <button className="btn btn-danger btn-sm"
                              title="Reject"
                              onClick={(e) => { e.stopPropagation(); openReviewDlg(leave, 'reject'); }}>
                              <i className="bi bi-x-lg"></i>
                            </button>
                          )}
                        </>
                      )}
                      <button className="btn btn-outline-secondary btn-sm"
                        title="View details"
                        onClick={(e) => { e.stopPropagation(); setExpandedId(expandedId === leave._id ? null : leave._id); }}>
                        <i className={`bi bi-chevron-${expandedId === leave._id ? 'up' : 'down'}`}></i>
                      </button>
                    </div>
                  </td>
                </tr>
                {expandedId === leave._id && (
                  <tr>
                    <td colSpan={8} className="bg-light">
                      <div className="px-2 py-2">
                        <div className="row g-3">
                          <div className="col-md-6">
                            <p className="text-muted mb-1" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>REASON</p>
                            <p className="mb-0" style={{ fontSize: 13 }}>{leave.reason}</p>
                            {leave.rejectionReason && (
                              <p className={`mb-0 mt-1 ${isAutoRejected ? 'text-warning' : 'text-danger'}`} style={{ fontSize: 12 }}>
                                {isAutoRejected ? 'Not approved: ' : 'Rejected: '}{leave.rejectionReason}
                              </p>
                            )}
                          </div>
                          <div className="col-md-6">
                            <p className="text-muted mb-1" style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>AUDIT LOG</p>
                            <AuditLog log={leave.auditLog || []} />
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  // ── Main render ────────────────────────────────────────────────────────────
  return (
    <div>
      <PageHeader
        title="Leave Management"
        icon="bi-calendar-x"
        subtitle={editLeave ? "Editing leave application" : "Apply for leave and track your requests"}
        actions={
          !editLeave && (
            <button className="btn btn-primary btn-sm" onClick={() => {
              setAdminEditMode(false);
              setEditLeave(null);
              setForm({ leaveType: 'casual', leaveDuration: 'full_day', startDate: '', endDate: '', reason: '', permissionHours: 0, isEmergency: false, emergencyNote: '', proofFile: null });
              setShowForm(s => !s);
            }}>
              <i className="bi bi-plus-lg me-1"></i>Apply Leave
            </button>
          )
        }
      />

      {/* Tabs */}
      <div className="card mb-3 border-0 shadow-sm">
        <div className="card-body p-0">
          <div className="d-flex border-bottom">
            {[
              { idx: 0, icon: 'bi-person-lines-fill', label: 'My Leaves' },
              ...(isManager ? [{ idx: 1, icon: 'bi-people-fill', label: 'All Leaves' }] : []),
            ].map(t => (
              <button key={t.idx} type="button" onClick={() => setTab(t.idx)}
                className="border-0 bg-transparent py-3 px-4"
                style={{
                  fontWeight: 600, fontSize: 13, cursor: 'pointer',
                  color: tab === t.idx ? '#6366f1' : '#64748b',
                  borderBottom: tab === t.idx ? '2px solid #6366f1' : '2px solid transparent',
                  marginBottom: -1,
                }}>
                <i className={`bi ${t.icon} me-2`}></i>{t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Apply Leave Form / Edit Leave Form */}
      {showForm && (
        <div className="card mb-4 border-0 shadow-sm">
          <div className="card-header bg-white d-flex justify-content-between align-items-center">
            <span className="fw-semibold">
              <i className={`bi ${editLeave ? 'bi-pencil' : 'bi-plus-circle'} me-2 text-primary`}></i>
              {editLeave ? (adminEditMode ? 'Admin Edit Leave' : 'Edit Leave') : 'Apply for Leave'}
            </span>
            <button className="btn-close" onClick={() => { setShowForm(false); setEditLeave(null); setAdminEditMode(false); setForm({ leaveType: 'casual', leaveDuration: 'full_day', startDate: '', endDate: '', reason: '', permissionHours: 0, isEmergency: false, emergencyNote: '', proofFile: null }); }}></button>
          </div>
          <div className="card-body">
            {adminEditMode && (
              <div className="alert alert-info py-2 small mb-3">
                You are editing this leave as admin. Approved leave changes will update work attendance and monthly report automatically.
              </div>
            )}
            <form onSubmit={editLeave ? updateLeave : submitLeave}>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label fw-medium">Leave Type *</label>
                  <select className="form-select" value={form.leaveType} onChange={e => f('leaveType', e.target.value)}>
                    {LEAVE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label fw-medium">Duration</label>
                  <select className="form-select" value={form.leaveDuration} onChange={e => f('leaveDuration', e.target.value)}>
                    {DURATION_OPTS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
                {form.leaveDuration === 'hours' ? (
                  <>
                    <div className="col-md-2">
                      <label className="form-label fw-medium">Date *</label>
                      <input type="date" className="form-control" value={form.startDate}
                        onChange={e => { f('startDate', e.target.value); f('endDate', e.target.value); }} required />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label fw-medium">Hours</label>
                      <input type="number" className="form-control" min={0.5} max={8} step={0.5}
                        value={form.permissionHours}
                        onChange={e => f('permissionHours', parseFloat(e.target.value))} />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="col-md-2">
                      <label className="form-label fw-medium">From *</label>
                      <input type="date" className="form-control" value={form.startDate}
                        onChange={e => f('startDate', e.target.value)} required />
                    </div>
                    <div className="col-md-2">
                      <label className="form-label fw-medium">To *</label>
                      <input type="date" className="form-control" value={form.endDate}
                        min={form.startDate}
                        onChange={e => f('endDate', e.target.value)} required />
                    </div>
                  </>
                )}
                <div className="col-12">
                  <label className="form-label fw-medium">Reason *</label>
                  <textarea className="form-control" rows={2} value={form.reason}
                    onChange={e => f('reason', e.target.value)} required
                    placeholder="Brief reason for leave..." />
                </div>
              </div>
              <div className="d-flex gap-2 justify-content-end mt-3">
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setShowForm(false); setEditLeave(null); setAdminEditMode(false); }}>Cancel</button>
                <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
                  {saving && <span className="spinner-border spinner-border-sm me-1"></span>}
                  {editLeave ? 'Save Changes' : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Tab: My Leaves */}
      {tab === 0 && (
        <div>
          {renderMyLeaves()}
        </div>
      )}

      {/* Tab: All Leaves (admin) */}
      {tab === 1 && isManager && (
        <div>
          {/* Filters */}
          <div className="card mb-3 border-0 shadow-sm">
            <div className="card-body py-2">
              <div className="d-flex gap-2 flex-wrap align-items-center">
                <select className="form-select form-select-sm" style={{ width: 160 }}
                  value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                  <option value="">All Statuses</option>
                  {Object.entries(STATUS_MAP).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
                <select className="form-select form-select-sm" style={{ width: 200 }}
                  value={filterEmployee} onChange={e => setFilterEmployee(e.target.value)}>
                  <option value="">All Employees</option>
                  {staff.map(s => (
                    <option key={s._id} value={s._id}>
                      {s.firstName} {s.lastName} ({s.roleKey})
                    </option>
                  ))}
                </select>
                <button className="btn btn-outline-secondary btn-sm" onClick={loadAllLeaves}>
                  <i className="bi bi-arrow-clockwise me-1"></i>Refresh
                </button>
              </div>
            </div>
          </div>
          <div className="card border-0 shadow-sm">
            <div className="card-body p-0">
              {renderAllLeaves()}
            </div>
          </div>
        </div>
      )}

      {/* Approve / Reject Modal */}
      {reviewDlg && (
        <div className="modal show d-block" tabIndex="-1" style={{ background: 'rgba(0,0,0,.45)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title text-capitalize">
                  <i className={`bi ${reviewDlg.action === 'approve' ? 'bi-check-circle text-success' : 'bi-x-circle text-danger'} me-2`}></i>
                  {reviewDlg.action} Leave
                </h5>
                <button className="btn-close" onClick={() => setReviewDlg(null)}></button>
              </div>
              <div className="modal-body">
                <p className="mb-2" style={{ fontSize: 13 }}>
                  <strong>Employee:</strong> {reviewDlg.leave.employee?.firstName} {reviewDlg.leave.employee?.lastName}<br />
                  <strong>Type:</strong> {LEAVE_TYPES.find(l => l.value === reviewDlg.leave.leaveType)?.label}<br />
                  <strong>Dates:</strong> {fmt(reviewDlg.leave.startDate)} → {fmt(reviewDlg.leave.endDate)} ({reviewDlg.leave.totalDays} days)
                </p>
                <div className="mb-3">
                  <label className="form-label fw-medium">
                    {reviewDlg.action === 'reject' ? 'Rejection Reason *' : 'Note (optional)'}
                  </label>
                  <textarea className="form-control" rows={3} value={reviewNote}
                    onChange={e => setReviewNote(e.target.value)}
                    placeholder={reviewDlg.action === 'reject' ? 'Reason for rejection...' : 'Optional note...'} />
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setReviewDlg(null)}>Cancel</button>
                <button
                  className={`btn btn-sm ${reviewDlg.action === 'approve' ? 'btn-success' : 'btn-danger'}`}
                  disabled={reviewSaving || (reviewDlg.action === 'reject' && !reviewNote.trim())}
                  onClick={submitReview}>
                  {reviewSaving && <span className="spinner-border spinner-border-sm me-1"></span>}
                  Confirm {reviewDlg.action.charAt(0).toUpperCase() + reviewDlg.action.slice(1)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AlertToast show={alert.show} message={alert.msg} type={alert.type}
        onClose={() => setAlert(a => ({ ...a, show: false }))} />
    </div>
  );
}
